import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PlanCard from '../components/Subscription/PlanCard';
import EmbeddedCheckoutModal from '../components/Subscription/EmbeddedCheckoutModal';
import { fetchPlans, fetchMySubscription, fetchPayments } from '../../store/actions/SubscriptionActions';
import {
  selectSubscription, selectIsSubscriptionActive, selectSubscriptionState,
  selectSubscriptionDaysRemaining, selectNeedsRenewal,
} from '../../store/selectors/SubscriptionSelectors';
import SubscriptionService from '../../services/SubscriptionService';
import { NAV_CATALOG, ALL_NAV_KEYS } from '../layouts/console/navModel';
import { PageHeader, Card, Button, Field, Input, inputClass, EmptyState, Toggle } from '../ui';

const ACCESS_FEATURES = [
  ['allow_custom_domains', 'White-label & custom domain', 'Own domain + logo upload'],
  ['allow_switcher', 'Multi-camera live switcher', 'Combine camera feeds, cut a program output'],
  ['allow_failover', 'Auto-failover', 'Loop VOD if the live encoder drops'],
  ['allow_playout', 'Playout calendar', 'Weekly/monthly drag-and-drop program scheduling'],
  ['allow_multistream', 'Multistream', 'Restream to YouTube, Facebook, Twitch simultaneously'],
  ['allow_iptv', 'IPTV channels', 'Create IPTV playlists'],
  ['allow_cdn_config', 'CDN configuration', 'Custom CDN / edge settings'],
  ['allow_team', 'Team management', 'Add camera operators who broadcast from the mobile app'],
];
const MONETIZATION_FEATURES = [
  ['allow_avod', 'AVOD'], ['allow_svod', 'SVOD'], ['allow_ads', 'Video ads'],
  ['allow_preroll', 'Pre-roll'], ['allow_midroll', 'Mid-roll'], ['allow_postroll', 'Post-roll'],
  ['allow_google_ads', 'Google Ad Manager'], ['allow_programmatic_ads', 'Programmatic ads'],
];
const FLAG_KEYS = [...ACCESS_FEATURES, ...MONETIZATION_FEATURES].map(([k]) => k);

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(isNaN(Number(d)) ? d : Number(d));
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const Pricing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [successMessage, setSuccessMessage] = useState(null);

  const [editPlan, setEditPlan] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [adminPlans, setAdminPlans] = useState([]); // full plans (with feature flags) for editing

  const plans = useSelector((s) => s.subscription.plans);
  const loading = useSelector((s) => s.subscription.plansLoading);
  const error = useSelector((s) => s.subscription.plansError);
  const { user } = useSelector((s) => s.auth);
  const subscription = useSelector(selectSubscription);
  const isEntitled = useSelector(selectIsSubscriptionActive);
  const subState = useSelector(selectSubscriptionState);
  const daysLeft = useSelector(selectSubscriptionDaysRemaining);
  const needsRenewal = useSelector(selectNeedsRenewal);

  const currentPlanId = subscription?.plan_id || subscription?.plan?.id || null;
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    dispatch(fetchPlans());
    if (user) dispatch(fetchMySubscription());
  }, [dispatch, user]);

  useEffect(() => {
    if (isAdmin) {
      SubscriptionService.getAllPlans()
        .then((r) => setAdminPlans(r.plans || []))
        .catch(() => {});
    }
  }, [isAdmin]);

  const priceOf = (plan) => (billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
  const currentPlan = plans?.find((p) => p.id === currentPlanId) || subscription?.plan || null;

  // Per-card CTA: 'subscribe' | 'current' | 'upgrade' | 'downgrade' | 'renew'
  const modeForPlan = (plan) => {
    const isThis = currentPlanId && plan.id === currentPlanId;
    if (needsRenewal) return isThis ? 'renew' : 'subscribe';
    if (!isEntitled) return 'subscribe';
    if (isThis) return subState === 'expiring_soon' ? 'renew' : 'current';
    if (currentPlan && priceOf(plan) > priceOf(currentPlan)) return 'upgrade';
    return 'downgrade';
  };

  const selectPlan = (plan, cycle) => {
    if (!user) return navigate('/login', { state: { from: '/pricing', planId: plan.id } });
    setSelectedPlan(plan);
    setBillingCycle(cycle);
    setShowCheckout(true);
  };

  const onCheckoutSuccess = (paymentIntent, plan) => {
    setShowCheckout(false);
    setSelectedPlan(null);
    localStorage.setItem('hasSubscription', 'true');
    setSuccessMessage(`You're on the ${plan?.name} plan. Enjoy!`);
    setTimeout(() => setSuccessMessage(null), 10000);
    setTimeout(() => {
      dispatch(fetchMySubscription());
      dispatch(fetchPayments());
      if (window.refreshSubscriptionStatus) window.refreshSubscriptionStatus();
    }, 600);
  };

  const openEdit = (plan) => {
    const full = adminPlans.find((p) => p.id === plan.id) || plan;
    setEditPlan(full);
    setEditForm({
      name: full.name || '',
      description: full.description || '',
      price_monthly: ((full.price_monthly || 0) / 100).toString(),
      price_yearly: ((full.price_yearly || 0) / 100).toString(),
      max_streams: full.max_streams ?? 0,
      max_storage_gb: full.max_storage_gb ?? 0,
      max_bandwidth_gb: full.max_bandwidth_gb ?? 0,
      max_iptv_channels: full.max_iptv_channels ?? 0,
      max_team_members: full.max_team_members ?? 0,
      is_active: full.is_active === undefined ? true : !!full.is_active,
      features: (full.features || []).join('\n'),
      // null / missing nav_items = show the whole menu
      nav_items: Array.isArray(full.nav_items) ? full.nav_items : [...ALL_NAV_KEYS],
      ...FLAG_KEYS.reduce((acc, k) => ({ ...acc, [k]: !!full[k] }), {}),
    });
    setEditError(null);
  };

  const toggleNav = (key) => setEditForm((p) => {
    const set = new Set(p.nav_items || ALL_NAV_KEYS);
    if (set.has(key)) set.delete(key); else set.add(key);
    return { ...p, nav_items: ALL_NAV_KEYS.filter((k) => set.has(k)) };
  });

  const editChange = (e) => {
    const { name, value } = e.target;
    const intFields = ['max_streams', 'max_storage_gb', 'max_bandwidth_gb', 'max_iptv_channels', 'max_team_members'];
    setEditForm((p) => ({ ...p, [name]: intFields.includes(name) ? (parseInt(value) || 0) : value }));
  };
  const toggleFlag = (key) => setEditForm((p) => ({ ...p, [key]: !p[key] }));

  const savePlan = async () => {
    setEditLoading(true);
    setEditError(null);
    try {
      const navSel = editForm.nav_items || [];
      await SubscriptionService.updatePlan(editPlan.id, {
        ...editForm,
        price_monthly: Math.round(parseFloat(editForm.price_monthly || 0) * 100),
        price_yearly: Math.round(parseFloat(editForm.price_yearly || 0) * 100),
        features: editForm.features.split('\n').map((f) => f.trim()).filter(Boolean),
        // all entries selected → null ("show everything", stays correct if new
        // menu items are added later); otherwise the explicit allow-list
        nav_items: navSel.length >= ALL_NAV_KEYS.length ? null : navSel,
      });
      dispatch(fetchPlans());
      if (isAdmin) SubscriptionService.getAllPlans().then((r) => setAdminPlans(r.plans || [])).catch(() => {});
      setEditPlan(null);
    } catch (err) {
      setEditError(err.message || 'Failed to update plan');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Plans & Pricing"
        subtitle="Choose the plan that fits your broadcasting needs"
        actions={
          <div className="tw-flex tw-rounded-[10px] tw-border tw-border-line tw-p-0.5">
            {['monthly', 'yearly'].map((c) => (
              <button
                key={c}
                onClick={() => setBillingCycle(c)}
                className={`tw-rounded-lg tw-px-4 tw-py-1.5 tw-text-sm tw-font-semibold tw-capitalize tw-transition ${
                  billingCycle === c ? 'tw-bg-brand tw-text-white' : 'tw-text-content-muted hover:tw-text-heading'
                }`}
              >
                {c}{c === 'yearly' && <span className="tw-ml-1 tw-text-xs tw-opacity-80">–17%</span>}
              </button>
            ))}
          </div>
        }
      />

      {isAdmin && (
        <div className="tw-mb-5 tw-rounded-xl tw-border tw-border-info/30 tw-bg-info/10 tw-px-4 tw-py-2.5 tw-text-sm tw-text-info">
          <i className="fa-solid fa-pen tw-mr-2" />Admin: use the edit icon on any plan to change pricing and features.
        </div>
      )}

      {subscription && currentPlan && (
        <div
          className={`tw-mb-6 tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-sm ${
            subState === 'expired' || subState === 'canceled'
              ? 'tw-border-live/30 tw-bg-live/10 tw-text-live'
              : subState === 'expiring_soon' || subState === 'canceling'
              ? 'tw-border-warn/30 tw-bg-warn/10 tw-text-warn'
              : 'tw-border-line tw-bg-raised tw-text-content'
          }`}
        >
          <i className={`fa-solid ${subState === 'expired' || subState === 'canceled' ? 'fa-circle-exclamation' : 'fa-circle-info'}`} />
          <span className="tw-mr-auto">
            {subState === 'expired' || subState === 'canceled' ? (
              <>Your <strong>{currentPlan.name}</strong> plan expired on <strong>{fmtDate(subscription.current_period_end)}</strong>. Renew to restore streaming.</>
            ) : subState === 'canceling' ? (
              <>Your <strong>{currentPlan.name}</strong> plan is set to cancel on <strong>{fmtDate(subscription.current_period_end)}</strong>.</>
            ) : subState === 'expiring_soon' ? (
              <>Your <strong>{currentPlan.name}</strong> plan renews in <strong>{daysLeft} day{daysLeft === 1 ? '' : 's'}</strong> ({fmtDate(subscription.current_period_end)}).</>
            ) : (
              <>You're on the <strong>{currentPlan.name}</strong> plan — renews {fmtDate(subscription.current_period_end)}. Pick a higher tier below to upgrade.</>
            )}
          </span>
          {(subState === 'expired' || subState === 'canceled' || subState === 'expiring_soon') && (
            <Button size="sm" icon="fa-rotate-right" onClick={() => selectPlan(currentPlan, billingCycle)}>
              Renew {currentPlan.name}
            </Button>
          )}
        </div>
      )}

      {successMessage && (
        <div className="tw-mb-5 tw-rounded-xl tw-border tw-border-good/30 tw-bg-good/10 tw-px-4 tw-py-3 tw-text-sm tw-text-good">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="tw-mb-5 tw-rounded-xl tw-border tw-border-live/30 tw-bg-live/10 tw-px-4 tw-py-3 tw-text-sm tw-text-live">
          {error}
        </div>
      )}

      {loading ? (
        <div className="tw-grid tw-gap-6 md:tw-grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="tw-h-96 tw-animate-pulse tw-rounded-2xl tw-border tw-border-line tw-bg-ink-900" />
          ))}
        </div>
      ) : plans && plans.length > 0 ? (
        <div className="tw-grid tw-gap-6 md:tw-grid-cols-2 xl:tw-grid-cols-3">
          {plans.map((plan, idx) => (
            <div key={plan.id} className="tw-relative">
              {isAdmin && (
                <button
                  className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-line tw-bg-ink-850 tw-text-content-muted hover:tw-text-heading"
                  onClick={() => openEdit(plan)}
                  title="Edit plan"
                >
                  <i className="fa-solid fa-pen" />
                </button>
              )}
              <PlanCard
                plan={plan}
                isPopular={idx === 1}
                onSelect={selectPlan}
                mode={modeForPlan(plan)}
                billingCycle={billingCycle}
              />
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState icon="fa-tags" title="No plans available" hint="Subscription plans haven't been configured yet." />
        </Card>
      )}

      {selectedPlan && (
        <EmbeddedCheckoutModal
          show={showCheckout}
          onHide={() => { setShowCheckout(false); setSelectedPlan(null); }}
          plan={selectedPlan}
          billingCycle={billingCycle}
          onSuccess={onCheckoutSuccess}
        />
      )}

      <Modal show={!!editPlan} onHide={() => setEditPlan(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="tw-flex tw-items-center tw-gap-2 tw-text-base">
            <i className="fa-solid fa-pen-to-square tw-text-brand" />
            Edit plan · <span className="tw-text-brand">{editPlan?.name}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="tw-space-y-7 tw-px-6 tw-py-5">
          {editError && (
            <div className="tw-rounded-lg tw-border tw-border-live/30 tw-bg-live/10 tw-px-3 tw-py-2 tw-text-sm tw-text-live">{editError}</div>
          )}

          {/* Details */}
          <section>
            <h4 className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Plan details</h4>
            <div className="tw-space-y-4">
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Plan name"><Input name="name" value={editForm.name || ''} onChange={editChange} placeholder="e.g. Professional" /></Field>
                <Field label="Status">
                  <div className="tw-flex tw-h-[42px] tw-items-center tw-gap-2.5 tw-rounded-[10px] tw-border tw-border-line-strong tw-bg-surface tw-px-3.5">
                    <Toggle checked={!!editForm.is_active} onChange={(v) => setEditForm((p) => ({ ...p, is_active: v }))} />
                    <span className="tw-text-sm tw-text-content">{editForm.is_active ? 'Published' : 'Hidden'}</span>
                  </div>
                </Field>
              </div>
              <Field label="Description">
                <Input name="description" value={editForm.description || ''} onChange={editChange} placeholder="Shown on the plan card" />
              </Field>
            </div>
          </section>

          {/* Pricing */}
          <section>
            <h4 className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Pricing</h4>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label="Monthly price" hint="In dollars">
                <div className="tw-relative">
                  <span className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-content-faint">$</span>
                  <Input type="number" step="0.01" min="0" name="price_monthly" value={editForm.price_monthly} onChange={editChange} className="tw-pl-7" />
                </div>
              </Field>
              <Field label="Yearly price" hint="In dollars">
                <div className="tw-relative">
                  <span className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw--translate-y-1/2 tw-text-content-faint">$</span>
                  <Input type="number" step="0.01" min="0" name="price_yearly" value={editForm.price_yearly} onChange={editChange} className="tw-pl-7" />
                </div>
              </Field>
            </div>
          </section>

          {/* Limits */}
          <section>
            <h4 className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Limits</h4>
            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
              <Field label="Live streams"><Input type="number" min="0" name="max_streams" value={editForm.max_streams} onChange={editChange} /></Field>
              <Field label="Storage (GB)"><Input type="number" min="0" name="max_storage_gb" value={editForm.max_storage_gb} onChange={editChange} /></Field>
              <Field label="Bandwidth (GB)"><Input type="number" min="0" name="max_bandwidth_gb" value={editForm.max_bandwidth_gb} onChange={editChange} /></Field>
            </div>
          </section>

          {/* Feature bullets */}
          <section>
            <h4 className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Feature list</h4>
            <Field hint="One per line — appears with a checkmark on the plan card.">
              <textarea className={inputClass} rows={4} name="features" value={editForm.features || ''} onChange={editChange} />
            </Field>
          </section>

          {/* Access */}
          <section>
            <h4 className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Access included in this plan</h4>
            <div className="tw-divide-y tw-divide-line tw-rounded-xl tw-border tw-border-line">
              {ACCESS_FEATURES.map(([key, label, hint]) => (
                <div key={key} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3">
                  <div>
                    <div className="tw-text-sm tw-font-semibold tw-text-heading">{label}</div>
                    <div className="tw-text-xs tw-text-content-faint">{hint}</div>
                  </div>
                  <Toggle checked={!!editForm[key]} onChange={() => toggleFlag(key)} />
                </div>
              ))}
              {editForm.allow_iptv && (
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3">
                  <div className="tw-text-sm tw-font-semibold tw-text-heading">Max IPTV channels</div>
                  <Input type="number" min="0" name="max_iptv_channels" value={editForm.max_iptv_channels}
                    onChange={editChange} className="tw-w-24 tw-text-center" />
                </div>
              )}
              {editForm.allow_team && (
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3">
                  <div>
                    <div className="tw-text-sm tw-font-semibold tw-text-heading">Max team members</div>
                    <div className="tw-text-xs tw-text-content-faint">0 = unlimited</div>
                  </div>
                  <Input type="number" min="0" name="max_team_members" value={editForm.max_team_members}
                    onChange={editChange} className="tw-w-24 tw-text-center" />
                </div>
              )}
            </div>
          </section>

          {/* Sidebar menu */}
          <section>
            <h4 className="tw-mb-1 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Sidebar menu</h4>
            <p className="tw-mb-3 tw-text-xs tw-text-content-faint">
              Choose which console menu items subscribers on this plan can see. Unchecked items are hidden from their sidebar. Admins always see everything.
            </p>
            <div className="tw-space-y-3 tw-rounded-xl tw-border tw-border-line tw-p-3">
              {NAV_CATALOG.map((group) => (
                <div key={group.section}>
                  <div className="tw-mb-1 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">{group.section}</div>
                  {group.items.map((it) => {
                    const on = (editForm.nav_items || []).includes(it.key);
                    return (
                      <div key={it.key}>
                        <label className="tw-flex tw-items-center tw-gap-2.5 tw-py-1 tw-text-sm tw-text-heading">
                          <input type="checkbox" checked={on} onChange={() => toggleNav(it.key)} />
                          {it.title}
                        </label>
                        {on && it.children.length > 0 && (
                          <div className="tw-ml-6 tw-border-l tw-border-line tw-pl-3">
                            {it.children.map((c) => (
                              <label key={c.key} className="tw-flex tw-items-center tw-gap-2.5 tw-py-0.5 tw-text-xs tw-text-content-muted">
                                <input
                                  type="checkbox"
                                  checked={(editForm.nav_items || []).includes(c.key)}
                                  onChange={() => toggleNav(c.key)}
                                />
                                {c.title}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </section>

          {/* Monetization */}
          <section>
            <h4 className="tw-mb-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Monetization</h4>
            <div className="tw-grid tw-gap-2 sm:tw-grid-cols-2">
              {MONETIZATION_FEATURES.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleFlag(key)}
                  className={`tw-flex tw-items-center tw-gap-2.5 tw-rounded-lg tw-border tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-transition ${
                    editForm[key]
                      ? 'tw-border-brand tw-bg-brand-soft tw-text-brand'
                      : 'tw-border-line tw-text-content-muted hover:tw-border-line-strong'
                  }`}
                >
                  <i className={`fa-solid ${editForm[key] ? 'fa-square-check' : 'fa-square'} tw-text-base`} />
                  {label}
                </button>
              ))}
            </div>
          </section>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditPlan(null)}>Cancel</Button>
          <Button variant="primary" icon={editLoading ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} onClick={savePlan} disabled={editLoading}>
            {editLoading ? 'Saving…' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Pricing;
